import React from 'react';
import { cva } from 'class-variance-authority';
import TextareaAutosize from 'react-textarea-autosize';
import { icons } from '../icons';
import { inputFrameClasses } from './KoobInputText';

type KoobPromptInputProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className' | 'style' | 'rows' | 'value' | 'onChange' | 'onSubmit'
> & {
  value: string;
  onValueChange: (value: string) => void;
  /** Enter (without Shift) or the send button; not called while empty or disabled. */
  onSubmit: (value: string) => void;
  /** Accessible name of the send button. */
  sendLabel: string;
  /** Small text left of the send button, e.g. how many questions are left. */
  hint?: React.ReactNode;
  maxRows?: number;
  /** sm: one line with the send button beside the text, for tight spaces. */
  size?: 'md' | 'sm';
};

// The frame shows focus, with the look of every Koob field
// (inputInteractionClasses).
const frameVariants = cva(
  `flex ring-offset-background transition-colors focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20 has-[textarea:disabled]:bg-gray-100 ${inputFrameClasses}`,
  {
    variants: {
      size: {
        md: 'flex-col gap-1.5 rounded-xl px-3 pb-2 pt-2.5',
        sm: 'items-end gap-2 rounded-lg py-1 pl-3 pr-1',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

const sendVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-gray-900 text-white transition-colors hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-30',
  {
    variants: { size: { md: 'size-8', sm: 'size-7 rounded-md text-xs' } },
    defaultVariants: { size: 'md' },
  },
);

/**
 * Chat composer, as in AI chat apps: a textarea that grows with its text and
 * a send button in the same frame. Enter sends, Shift+Enter adds a line.
 */
const KoobPromptInput = React.forwardRef<
  HTMLTextAreaElement,
  KoobPromptInputProps
>(
  (
    {
      value,
      onValueChange,
      onSubmit,
      sendLabel,
      hint,
      disabled,
      maxRows = 6,
      size = 'md',
      ...props
    },
    ref,
  ) => {
    const canSend = !disabled && value.trim() !== '';
    const send = () => canSend && onSubmit(value);
    const sendButton = (
      <button
        type="button"
        onClick={send}
        disabled={!canSend}
        aria-label={sendLabel}
        title={sendLabel}
        className={sendVariants({ size })}
      >
        <i className={icons.send} aria-hidden="true" />
      </button>
    );

    // @tailwindcss/forms rings any focused textarea, so the textarea turns
    // that ring off: the frame shows focus.
    return (
      <div className={frameVariants({ size })}>
        <TextareaAutosize
          ref={ref}
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={(event) => {
            if (
              event.key === 'Enter' &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              send();
            }
          }}
          disabled={disabled}
          minRows={1}
          maxRows={maxRows}
          className={`block w-full resize-none border-none bg-transparent p-0 text-sm leading-relaxed text-foreground shadow-none outline-none placeholder:text-muted-foreground focus:shadow-none focus:outline-none focus:ring-0 disabled:cursor-not-allowed ${size === 'sm' ? 'self-center py-1' : ''}`}
          {...props}
        />
        {size === 'sm' ? (
          // ponytail: no hint line in sm; add one when a tight space needs it
          sendButton
        ) : (
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">{hint}</span>
            {sendButton}
          </div>
        )}
      </div>
    );
  },
);

KoobPromptInput.displayName = 'KoobPromptInput';

export default KoobPromptInput;
